/* eslint-disable no-unused-expressions */
import React, { memo, useState } from 'react'
import {
  OutlinedInput as Input,
  Chip,
  InputLabel,
  FormControl,
  Typography,
} from '@maas-components/core'
import { AutocompleteGetTagProps } from '@maas-components/labs'
import { Clear } from '@material-ui/icons'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Autocomplete, { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete'
import lodash from 'lodash'
import { FixedSizeList } from 'react-window'
import i18n from 'core/common/i18n'
import { useAppContext } from 'core/pages/ProductNew-v2/context/AppContext'
import { normalizeString } from 'netshoes/utils/string'
import { filterOptions } from './helper'
import Option from './Option'
import { StyledFormHelperText, StyledInputContainer } from './style'
import { IOptionProps, VariationsAutocompleteFieldProps } from './types'
import VariationAvatar from './VariationAvatar'

function VariationsAutocompleteField(props: VariationsAutocompleteFieldProps) {
  const { blockFieldsVariations } = useAppContext()
  const [noResults, setNoResults] = useState(false)

  const {
    value,
    onChange,
    options = [],
    defaultValue = [],
    placeholder = '',
    inputLabel = '',
    maxLength,
    autoSelect = false,
    blurOnSelect = false,
    showAvatar = false,
    openOnFocus = true,
    errorMessage = '',
    error = false,
    className = '',
  } = props

  const uniqueOptions = lodash.uniqBy(options, 'title')

  const renderTags = (values: IOptionProps[], getTagProps: AutocompleteGetTagProps) =>
    values.map((value, index) => {
      const tagProps = getTagProps({ index })

      return (
        <Chip
          {...tagProps}
          key={`tag-${value.title}`}
          label={value.title}
          deleteIcon={<Clear data-testid="tag-delete-icon" />}
          avatar={<VariationAvatar option={value} showAvatar={showAvatar} />}
          data-testid={`autocomplete-tag-${value.title}`}
          {...tagProps}
        />
      )
    })

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const { InputLabelProps, InputProps, inputProps, ...otherProps } = params
    const { ref, endAdornment, ...otherInputProps } = InputProps

    const onInput = (event: React.FormEvent<HTMLInputElement>) => {
      const inputElement = event.currentTarget
      const sanitizedValue = inputElement.value.replace(/[@#%$&!*()[\]\-{}=:¨"'¹²³£¢¬]/g, '')

      if (sanitizedValue !== inputElement.value) {
        inputElement.value = sanitizedValue
      }
    }

    return (
      <StyledInputContainer ref={ref}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel {...InputLabelProps} error={error}>
            {inputLabel}
          </InputLabel>
          <Input
            {...otherProps}
            {...otherInputProps}
            inputProps={{ ...inputProps, onInput, maxLength, 'data-testid': 'autocomplete-input' }}
            placeholder={placeholder}
            error={error}
          />
        </FormControl>
        {error && (
          <StyledFormHelperText>
            <Typography
              className="d-flex align-center"
              color="error"
              variant="caption"
              paragraph={false}
            >
              <InfoOutlinedIcon fontSize="small" />
              {errorMessage || i18n.t('productNew.variations.insertVariationValueErrorMessage')}
            </Typography>
          </StyledFormHelperText>
        )}
      </StyledInputContainer>
    )
  }

  const Row = ({
    data,
    index,
    style,
  }: {
    data: unknown[]
    index: number
    style: React.CSSProperties
  }) => {
    const option = data[index]
    return <div style={style}>{option}</div>
  }

  const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
      const { children, ...other } = props
      const options = React.Children.toArray(children)

      return (
        <div ref={ref} {...other} className="w-100">
          <FixedSizeList
            height={317}
            width="100%"
            itemCount={options.length}
            itemSize={40}
            overscanCount={10}
          >
            {({ index, style }) => <Row data={options} index={index} style={style} />}
          </FixedSizeList>
        </div>
      )
    }
  )

  return (
    <Autocomplete
      multiple
      freeSolo
      clearOnBlur
      blurOnSelect={blurOnSelect}
      options={uniqueOptions}
      value={value}
      defaultValue={defaultValue}
      openOnFocus={openOnFocus}
      ListboxComponent={ListboxComponent}
      onChange={(event, newValue, reason) => {
        if (
          reason === 'select-option' &&
          newValue.some(option => typeof option !== 'string' && option.value === 'not_found')
        ) {
          return
        }
        onChange(event, newValue, reason)
      }}
      renderTags={renderTags}
      renderInput={renderInput}
      autoSelect={autoSelect}
      onInputChange={(event, inputValue) => {
        const filteredOptions = options.filter(option =>
          option.title.toLowerCase().includes(inputValue.toLowerCase())
        )
        setNoResults(filteredOptions.length === 0)
      }}
      filterOptions={
        blockFieldsVariations
          ? (renderedOptions, state) => {
              if (state.inputValue.trim().length < 1) {
                return lodash.uniqBy(renderedOptions, 'title')
              }

              const filtered = lodash.filter(options, item =>
                normalizeString(item.title.toLowerCase()).startsWith(
                  normalizeString(state.inputValue.toLowerCase())
                )
              )

              const sliceFilteredBrands = filtered.length
                ? lodash.uniqBy(filtered, 'title')
                : [
                    {
                      value: 'not_found',
                      title: 'Item pesquisado não encontrado.',
                    },
                  ]

              return sliceFilteredBrands
            }
          : filterOptions
      }
      getOptionLabel={option => option.title}
      renderOption={option => (
        <Option noResults={noResults} option={option} showAvatar={showAvatar} />
      )}
      filterSelectedOptions
      autoHighlight
      className={className}
    />
  )
}

export default memo(VariationsAutocompleteField)
